import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../AppContextProvider";
import spatialService from "../services/spatialService";
import dealsService from "../services/dealsService";
import { OCC_STATE } from "../utils/occUnits";
import { OCC_KEEP_MS, occCountyKey, pickFC } from "./useOccUnits";
import { sessionCache } from "../utils/sessionCache";
import { readPersisted, writePersisted } from "../utils/persistentCache";

const EMPTY_FC = { type: "FeatureCollection", features: [] };

// `${oid}|intents|${county}` -> FeatureCollection, for the life of the tab
// (utils/sessionCache), so reopening a map refetches nothing; the browser also
// keeps each county's for OCC_KEEP_MS (daily ETL, see useOccUnits).
const cache = sessionCache();

/**
 * OCC intents to drill (well permits) for a SET of counties, merged.
 *
 * Only Oklahoma counties are requested (the endpoint is OK-only), one
 * request per county, in parallel. Cache lives as long as the tab, keyed by
 * (user, county), so toggling the overlay, narrowing the county set or
 * reopening the map never refetches; kept in the browser between visits for
 * OCC_KEEP_MS (a 404 is not kept). Nothing is fetched unless `enabled`.
 *
 * Also loads the `occ_intents` field metadata (popup labels) the first time
 * anything is requested.
 *
 * @param {{ counties?: Array<{ state: string, county: string }>,
 *   enabled?: boolean }} args
 * @returns {{ intents: object, metadata: Array<object>, loading: boolean,
 *   errorMsg: string|null }}
 */
const useOccIntents = ({ counties = [], enabled: wanted = false } = {}) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const oid = user?.idTokenClaims?.oid;

  const [intents, setIntents] = useState(EMPTY_FC);
  const [metadata, setMetadata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const metaRequestedRef = useRef(false);

  const countyKey = useMemo(() => occCountyKey(counties), [counties]);
  const enabled = !!countyKey && !!wanted;

  const resolveToken = async () => {
    let t = token;
    if (!t || (isTokenExpired && isTokenExpired(t))) t = await getToken();
    return t;
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!enabled) {
        setIntents(EMPTY_FC);
        setErrorMsg(null);
        return;
      }
      if (!oid) return;
      const countyList = countyKey.split(",");
      const keyOf = (county) => `${oid}|intents|${county}`;
      try {
        // Copies kept from an earlier visit fill the tab cache first.
        await Promise.all(
          countyList
            .filter((c) => !cache.has(keyOf(c)))
            .map(async (c) => {
              const kept = await readPersisted(keyOf(c));
              if (kept) cache.set(keyOf(c), kept);
            })
        );
        const missing = countyList.filter((c) => !cache.has(keyOf(c)));
        if (missing.length > 0) {
          setLoading(true);
          setErrorMsg(null);
          const t = await resolveToken();
          await Promise.all(
            missing.map(async (county) => {
              try {
                const resp = await spatialService.getOccIntents(t, oid, {
                  state: OCC_STATE,
                  county,
                });
                const fc = pickFC(resp);
                cache.set(keyOf(county), fc);
                writePersisted(keyOf(county), fc, OCC_KEEP_MS);
              } catch (err) {
                // 404 = nothing for this county; cache as empty.
                if (err?.response?.status !== 404) throw err;
                cache.set(keyOf(county), EMPTY_FC);
              }
            })
          );
        }
        if (!cancelled) {
          setIntents({
            type: "FeatureCollection",
            features: countyList.flatMap((c) => (cache.get(keyOf(c)) ?? EMPTY_FC).features),
          });
        }
      } catch (err) {
        if (!cancelled) setErrorMsg(err?.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, countyKey, oid]);

  // Popup metadata — once, the first time the overlay is requested. A
  // failure leaves the popup with title-cased field names; it never blocks
  // the map.
  useEffect(() => {
    if (!enabled || !oid || metaRequestedRef.current) return;
    metaRequestedRef.current = true;
    (async () => {
      try {
        const t = await resolveToken();
        const resp = await dealsService.getMetadata({
          token: t,
          userOid: oid,
          tableName: "occ_intents",
        });
        const rows = resp?.data?.data;
        setMetadata(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.warn("[useOccIntents] occ_intents metadata unavailable:", err?.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, oid]);

  return { intents, metadata, loading, errorMsg };
};

export default useOccIntents;
